import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import RichTextEditor from '@/components/RichTextEditor';
import MessagesTab from '@/components/templates/MessagesTab';
import {
  Plus, Pencil, Trash2, X, MapPin, Ship, FileStack, Loader2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPES = [
  { value: 'beach', label: 'Beach' },
  { value: 'mountain', label: 'Mountain' },
  { value: 'city', label: 'City' },
  { value: 'cruise', label: 'Cruise' },
  { value: 'wildlife', label: 'Wildlife' },
  { value: 'other', label: 'Other' },
];

const TYPE_STYLES = {
  beach: 'bg-amber-100 text-amber-700',
  mountain: 'bg-emerald-100 text-emerald-700',
  city: 'bg-sky-100 text-sky-700',
  cruise: 'bg-cyan-100 text-cyan-700',
  wildlife: 'bg-lime-100 text-lime-700',
  other: 'bg-slate-100 text-slate-600',
};

const EMPTY_FORM = {
  destination_name: '',
  destination_type: 'other',
  inclusions: [],
  exclusions: [],
  terms_and_conditions: '',
  is_default: false,
};

// ---- Editable tag-style list (add / remove / edit individual items) -------
const TagListInput = ({ items, onChange, accent = 'inclusion', testId }) => {
  const [draft, setDraft] = useState('');
  const tone = accent === 'inclusion'
    ? 'border-emerald-200 focus-within:border-emerald-400'
    : 'border-rose-200 focus-within:border-rose-400';

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...items, v]);
    setDraft('');
  };
  const removeAt = (i) => onChange(items.filter((_, x) => x !== i));
  const editAt = (i, v) => onChange(items.map((it, x) => (x === i ? v : it)));

  return (
    <div className="space-y-2" data-testid={testId}>
      {items.length === 0 && (
        <p className="text-xs text-slate-400 italic">No items yet — add below.</p>
      )}
      {items.map((it, i) => (
        <div key={i} className={`flex items-center gap-2 rounded-lg border ${tone} bg-white px-2 py-1`}>
          <Input
            value={it}
            onChange={(e) => editAt(i, e.target.value)}
            className="h-8 border-0 shadow-none focus-visible:ring-0 px-1 text-sm"
            data-testid={`${testId}-item-${i}`}
          />
          <button
            type="button"
            onClick={() => removeAt(i)}
            className="text-slate-400 hover:text-rose-600 shrink-0"
            data-testid={`${testId}-remove-${i}`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Add an item and press Enter…"
          className="h-9 text-sm"
          data-testid={`${testId}-input`}
        />
        <Button type="button" variant="outline" onClick={add} className="h-9 shrink-0" data-testid={`${testId}-add`}>
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
};

// ---- Create / Edit dialog --------------------------------------------------
const TemplateDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [form, setForm] = useState(() => (initial ? { ...EMPTY_FORM, ...initial } : EMPTY_FORM));
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);

  const set = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const save = async () => {
    if (!form.destination_name.trim()) {
      toast.error('Destination name is required');
      return;
    }
    setSaving(true);
    const payload = {
      destination_name: form.destination_name.trim(),
      destination_type: form.destination_type,
      inclusions: form.inclusions,
      exclusions: form.exclusions,
      terms_and_conditions: form.terms_and_conditions,
      is_default: form.is_default,
    };
    try {
      if (isEdit) {
        await axios.put(`${BACKEND_URL}/api/templates/destinations/${initial.id}`, payload);
        toast.success('Template updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/templates/destinations`, payload);
        toast.success('Template created');
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Destination Template' : 'New Destination Template'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Destination name *</Label>
              <Input
                value={form.destination_name}
                onChange={(e) => set('destination_name', e.target.value)}
                placeholder="e.g. Maldives, Royal Caribbean, Vietnam…"
                className="h-10"
                data-testid="template-name-input"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Type</Label>
              <Select value={form.destination_type} onValueChange={(v) => set('destination_type', v)}>
                <SelectTrigger className="h-10" data-testid="template-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value} data-testid={`template-type-${t.value}`}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label className="text-sm font-semibold text-emerald-700 mb-1.5 block">Inclusions</Label>
              <TagListInput items={form.inclusions} onChange={(v) => set('inclusions', v)} accent="inclusion" testId="template-inclusions" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-rose-700 mb-1.5 block">Exclusions</Label>
              <TagListInput items={form.exclusions} onChange={(v) => set('exclusions', v)} accent="exclusion" testId="template-exclusions" />
            </div>
          </div>

          <div>
            <Label className="text-sm font-semibold text-slate-700 mb-1.5 block">Terms &amp; Conditions</Label>
            <RichTextEditor
              value={form.terms_and_conditions}
              onChange={(v) => set('terms_and_conditions', v)}
              placeholder="Cancellation policy, payment terms…"
              minHeight={140}
              testId="template-tnc"
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-slate-700">Set as default for this destination</p>
              <p className="text-xs text-slate-500">Used first when auto-matching a destination on an itinerary.</p>
            </div>
            <Switch checked={form.is_default} onCheckedChange={(v) => set('is_default', v)} data-testid="template-default-switch" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="template-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="template-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            {isEdit ? 'Save changes' : 'Create template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Destinations tab ------------------------------------------------------
const DestinationsTab = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/templates/destinations`);
      setTemplates(Array.isArray(res.data) ? res.data : []);
    } catch {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/templates/destinations`);
        if (active) setTemplates(Array.isArray(res.data) ? res.data : []);
      } catch {
        toast.error('Failed to load templates');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/templates/destinations/${deleteTarget.id}`);
      toast.success('Template deleted');
      setDeleteTarget(null);
      load();
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (t) => { setEditing(t); setDialogOpen(true); };

  return (
    <div className="space-y-4" data-testid="templates-destinations-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{templates.length} destination template{templates.length === 1 ? '' : 's'}</p>
        <Button onClick={openNew} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="add-destination-template-btn">
          <Plus className="h-4 w-4 mr-1" /> Add New Destination
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-16 text-slate-500">No templates yet. Click &quot;Add New Destination&quot;.</div>
      ) : (
        <div className="rounded-xl border border-slate-200 overflow-hidden bg-white" data-testid="destination-templates-list">
          {templates.map((t, i) => (
            <div
              key={t.id}
              className={`flex items-center gap-3 px-4 py-3 ${i > 0 ? 'border-t border-slate-100' : ''} hover:bg-slate-50`}
              data-testid={`destination-template-row-${t.id}`}
            >
              <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${t.destination_type === 'cruise' ? 'bg-cyan-50 text-cyan-600' : 'bg-teal-50 text-teal-600'}`}>
                {t.destination_type === 'cruise' ? <Ship className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-slate-800 truncate" data-testid={`template-row-name-${t.id}`}>{t.destination_name}</p>
                  <Badge className={`${TYPE_STYLES[t.destination_type] || TYPE_STYLES.other} border-0 text-[11px] capitalize`}>{t.destination_type}</Badge>
                  {t.is_default && <Badge className="bg-teal-100 text-teal-700 border-0 text-[11px]">Default</Badge>}
                </div>
                <p className="text-xs text-slate-400 mt-0.5">
                  {t.inclusions?.length || 0} inclusions · {t.exclusions?.length || 0} exclusions · Updated {t.updatedAt ? new Date(t.updatedAt).toLocaleDateString() : '—'}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => openEdit(t)} className="text-slate-500 hover:text-teal-600" data-testid={`edit-template-${t.id}`}>
                <Pencil className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(t)} className="text-slate-500 hover:text-rose-600" data-testid={`delete-template-${t.id}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {dialogOpen && (
        <TemplateDialog open onOpenChange={setDialogOpen} initial={editing} onSaved={load} />
      )}

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-template-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &quot;{deleteTarget?.destination_name}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>This template will be permanently removed. Itineraries that already used it are not affected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-template-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="delete-template-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const ComingSoon = ({ icon: Icon, title, testId, showAdd }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center" data-testid={testId}>
    <span className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 mb-4">
      <Icon className="h-7 w-7" />
    </span>
    <h3 className="text-lg font-semibold text-slate-700">{title}</h3>
    <p className="text-sm text-slate-400 mt-1 max-w-sm">This section is on the way. Hang tight.</p>
    {showAdd && (
      <Button variant="outline" disabled className="mt-5 opacity-60" data-testid={`${testId}-add-btn`}>
        <Plus className="h-4 w-4 mr-1" /> Add Template
      </Button>
    )}
  </div>
);

export default function Templates() {
  const [params] = useSearchParams();
  const initialTab = ['destinations', 'messages', 'documents'].includes(params.get('tab')) ? params.get('tab') : 'destinations';
  const [tab, setTab] = useState(initialTab);
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto" data-testid="templates-page">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900">Templates</h1>
        <p className="text-sm text-slate-500 mt-1">Reusable destination inclusions, exclusions &amp; terms you can apply to any itinerary.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-5" data-testid="templates-tabs">
          <TabsTrigger value="destinations" data-testid="tab-destinations">Destinations</TabsTrigger>
          <TabsTrigger value="messages" data-testid="tab-messages">Messages</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents-templates">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="destinations"><DestinationsTab /></TabsContent>
        <TabsContent value="messages"><MessagesTab /></TabsContent>
        <TabsContent value="documents">
          <ComingSoon icon={FileStack} title="Document templates coming soon" testId="documents-coming-soon" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
